import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Users, ImagePlus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminHosts() {
  const [hosts, setHosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', bio: '', image_url: '' });
  const [uploadingImage, setUploadingImage] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  const fetchHosts = () => {
    axios.get(`${API}/hosts`).then(r => setHosts(r.data.hosts || [])).catch(() => {});
  };

  useEffect(() => { fetchHosts(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/admin/hosts/${editing}`, form);
        toast.success('Host updated');
      } else {
        await axios.post(`${API}/admin/hosts`, form);
        toast.success('Host created');
      }
      setShowForm(false); setEditing(null);
      setForm({ name: '', bio: '', image_url: '' });
      fetchHosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    setConfirmingDelete(id);
  };

  const executeDelete = async () => {
    if (!confirmingDelete) return;
    try {
      await axios.delete(`${API}/admin/hosts/${confirmingDelete}`);
      toast.success('Host deleted');
      fetchHosts();
    } catch { toast.error('Failed to delete'); }
    setConfirmingDelete(null);
  };

  const startEdit = (host) => {
    setForm({ name: host.name, bio: host.bio, image_url: host.image_url || '' });
    setEditing(host.id);
    setShowForm(true);
  };

  const handleImageUpload = async (hostId, file) => {
    setUploadingImage(hostId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/admin/hosts/${hostId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Host image uploaded');
      fetchHosts();
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };

  const getImageSrc = (host) => {
    if (host.image_data && host.image_data.startsWith('data:')) return host.image_data;
    if (host.image_storage_path) return `${API}/host-image/${host.id}`;
    if (host.image_url && !host.image_url.startsWith('__storage__')) return host.image_url;
    return 'https://images.unsplash.com/photo-1581092800573-6afa755dcdc7?w=400';
  };

  return (
    <div data-testid="admin-hosts">
      {confirmingDelete && <DeleteConfirmModal message="Delete this host? This cannot be undone." onConfirm={executeDelete} onCancel={() => setConfirmingDelete(null)} />}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>Hosts & DJs</h1>
          <p className="text-[#94A3B8] text-sm mt-1">{hosts.length} hosts</p>
        </div>
        <button
          data-testid="add-host-button"
          onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: '', bio: '', image_url: '' }); }}
          className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] transition-colors rounded-full px-6 py-3 shadow-lg shadow-[#D4AF37]/20 flex items-center gap-2 active:scale-95"
        >
          <Plus size={18} /> Add Host
        </button>
      </div>

      {showForm && (
        <form data-testid="host-form" onSubmit={handleSubmit} className="bg-[#171A21] rounded-2xl border border-white/5 p-6 mb-8 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Name *</label>
              <input
                data-testid="host-name-input"
                type="text"
                value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                placeholder="DJ name"
                className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Image URL</label>
              <input
                data-testid="host-image-input"
                type="text"
                value={form.image_url}
                onChange={e => setForm({...form, image_url: e.target.value})}
                placeholder="https://..."
                className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Bio</label>
            <textarea
              data-testid="host-bio-input"
              value={form.bio}
              onChange={e => setForm({...form, bio: e.target.value})}
              placeholder="Short biography..."
              rows={3}
              className="bg-[#0A0C10] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600 resize-none"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="bg-white/5 text-white font-medium hover:bg-white/10 border border-white/10 rounded-full px-6 py-2.5">Cancel</button>
            <button data-testid="host-submit-button" type="submit" className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] rounded-full px-6 py-2.5">{editing ? 'Update' : 'Create'} Host</button>
          </div>
        </form>
      )}

      {/* Hosts Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {hosts.map((host, i) => (
          <div key={host.id} data-testid={`admin-host-${i}`} className="bg-[#171A21] rounded-2xl border border-white/5 overflow-hidden hover:-translate-y-0.5 transition-transform">
            <div className="h-40 overflow-hidden relative group">
              <img
                src={getImageSrc(host)}
                alt={host.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <label className="cursor-pointer flex items-center gap-2 bg-[#D4AF37] text-[#0F1115] font-semibold rounded-full px-4 py-2 text-sm active:scale-95">
                  {uploadingImage === host.id ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                  {uploadingImage === host.id ? 'Uploading...' : 'Upload Photo'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid={`upload-host-image-${i}`}
                    onChange={e => e.target.files[0] && handleImageUpload(host.id, e.target.files[0])}
                  />
                </label>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-white font-semibold">{host.name}</h3>
                <div className="flex gap-1">
                  <button data-testid={`edit-host-${i}`} onClick={() => startEdit(host)} className="p-1.5 rounded-lg hover:bg-white/10 text-[#94A3B8]"><Edit2 size={14} /></button>
                  <button data-testid={`delete-host-${i}`} onClick={() => handleDelete(host.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-[#94A3B8] hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-[#94A3B8] text-sm line-clamp-3">{host.bio}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
